import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AppInput from "../../components/Core/AppInput/AppInput";
import Flex from "../../components/Layout/Flex";
import { DocumentLinkEditBreadcrumbs } from "../../configs/breadcrumbNavigation";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import { Params, useParams } from "react-router-dom";
import Button from "../../components/Core/Button/Button";
import { FormLabel } from "../../components/Core/AppText/AppText";
import Checkbox from "../../components/Core/Checkbox/Checkbox";
import {
	useCompetence,
	useDocumentInfo,
	useUpdateDocuments,
} from "../../networking/organisation.hooks";
import { Alert, AlertDescription, useToast } from "@chakra-ui/react";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import { validateAndFormatURL } from "../../utils/string.utils";

const DocumentLinkEditPage: React.FC = () => {
	const { t } = useTranslation();
	const { documentId } = useParams<keyof Params>() as Params;
	const toast = useToast();
	const { data: competenceList, isLoading: competenceLoading } = useCompetence(true);

	const [allBranches, setAllBranches] = useState<boolean>(false);

	const { data: documentInfo, isLoading: documentLoading } = useDocumentInfo(true, documentId);
	const [link, setLink] = useState<string>(documentInfo?.url || "");
	const [selectedBranches, setSelectedBranches] = useState<Array<string>>(
		documentInfo?.branches || []
	);
	const [title, setTitle] = useState<string>(documentInfo?.title || "");

	useEffect(() => {
		if (documentInfo) {
			setLink(documentInfo.url);
			setSelectedBranches(documentInfo?.branches || []);
			setTitle(documentInfo.title);

			if (documentInfo.branches.length === 0) {
				setAllBranches(true);
			} else {
				setAllBranches(false);
			}
		}
	}, [documentInfo]);

	const {
		mutate: publishDoc,
		isLoading: publishing,
		isSuccess: documentPublished,
		isError: documentPublishError,
	} = useUpdateDocuments();

	function publishDocument() {
		if ((selectedBranches.length > 0 || allBranches) && link && title) {
			const params: {
				data: {
					branches: Array<string>;
					mimeType: string;
					title: string;
					url: string;
				};
				documentId: string;
			} = {
				data: {
					branches: allBranches ? [] : selectedBranches,
					mimeType: "",
					title: title,
					url: validateAndFormatURL(link),
				},
				documentId: documentId || "",
			};
			publishDoc(params);
		} else {
			toast({
				id: "documentPublish validation",
				render: () => (
					<Alert status="warning" borderWidth={2} borderRadius={4} variant={"solid"}>
						<AlertDescription>{t("DOCUMENT_FILL_IN_MESSAGE")}</AlertDescription>
					</Alert>
				),
				isClosable: true,
				position: "bottom-right",
				duration: 2 * 1000,
			});
		}
	}

	useEffect(() => {
		if (documentPublished) {
			toast({
				id: "documentPublished",
				render: () => (
					<Alert status="success" borderWidth={2} borderRadius={4} variant={"solid"}>
						<AlertDescription>{t("DOCUMENT_UPDATE_MESSAGE_SUCCESS")}</AlertDescription>
					</Alert>
				),
				isClosable: true,
				position: "top",
				duration: 3 * 1000,
			});
		}
		if (documentPublishError) {
			toast({
				id: "documentPublished error",
				render: () => (
					<Alert status="error" borderWidth={2} borderRadius={4} variant={"solid"}>
						<AlertDescription>{t("DOCUMENT_UPDATE_MESSAGE_ERROR")}</AlertDescription>
					</Alert>
				),
				isClosable: true,
				position: "top",
				duration: 3 * 1000,
			});
		}
	}, [documentPublished, documentPublishError]);

	return (
		<>
			<Breadcrumbs items={DocumentLinkEditBreadcrumbs} />
			<div className="p-4 px-6">
				{documentLoading ? (
					<LoadingIndicator />
				) : (
					<Flex direction="column" width="50%">
						<Flex margin="0 0 10px 0">
							<AppInput
								label={t("LINK")}
								margin="0.5rem 0"
								placeholder={"URL_PLACEHOLDER"}
								value={link}
								onChange={(e: any) => setLink(e.target.value)}
							/>
						</Flex>

						<Flex direction="column">
							<AppInput
								label={t("TITLE")}
								margin="0.5rem 0"
								placeholder={t("TITLE")}
								value={title}
								onChange={(e: any) => setTitle(e.target.value)}
							/>
						</Flex>
						<Flex direction="column" margin="0 1rem 0 0">
							<FormLabel>{t("showConsultants")}</FormLabel>
							<Checkbox
								margin="0.5rem 0"
								label="Alla branscher"
								isChecked={allBranches}
								onChange={(e: any) => {
									setAllBranches(e.target.checked);
									if (e.target.checked) {
										setSelectedBranches([]);
									}
								}}
							/>
						</Flex>
						<Flex direction="column" margin="0 1rem 0 1rem">
							{competenceLoading ? (
								<LoadingIndicator />
							) : (
								competenceList?.competences &&
								Array.isArray(competenceList.competences) &&
								competenceList.competences.map((compt) => {
									return (
										<Checkbox
											margin="0.5rem 0"
											label={compt.name.sv}
											value={compt.key}
											isChecked={selectedBranches.includes(compt.key)}
											disabled={allBranches}
											onChange={(e: any) => {
												e.target.checked
													? setSelectedBranches([...selectedBranches, compt.key])
													: setSelectedBranches(
															selectedBranches.filter((branch) => branch !== compt.key)
													  );
											}}
										/>
									);
								})
							)}
						</Flex>
					</Flex>
				)}
				<Flex direction="row" width="100%" justifyContent="center" margin="2rem 10rem 0 0rem">
					<Button width="auto" loading={publishing} onClick={() => publishDocument()}>
						{t("updateDocuments")}
					</Button>
				</Flex>
			</div>
		</>
	);
};

export default DocumentLinkEditPage;
